import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import moment from "moment"
import toast from "react-hot-toast"

import { useAuth } from "contexts/auth-context"
import { useStockOpnameDetailRepositories } from "repositories/stock-opname"
import { extractApiErrorMessage } from "helpers/general-helper"

const STATUS_BADGE = {
  ACTIVE: "bg-amber-50 text-amber-700 border-amber-200",
  SUBMITTED: "bg-blue-50 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
  POSTED: "bg-green-50 text-green-700 border-green-200",
  CANCELED: "bg-neutral-50 text-neutral-700 border-neutral-200",
}

const STATUS_LABEL = {
  ACTIVE: "Aktif (input cek)",
  SUBMITTED: "Menunggu approval HO",
  APPROVED: "Disetujui HO",
  REJECTED: "Ditolak HO",
  POSTED: "Diposting",
  CANCELED: "Dibatalkan",
}

export default function Form() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const { data, isLoading, mutate } = useStockOpnameDetailRepositories(id)
  const [actionLoading, setActionLoading] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null) // 'post' | 'cancel' | 'submit' | 'approve' | 'reject'
  const [cancelReason, setCancelReason] = useState("")
  const [rejectReason, setRejectReason] = useState("")
  const [showAddCheck, setShowAddCheck] = useState(false)
  const [newCheckNotes, setNewCheckNotes] = useState("")
  const [creatingCheck, setCreatingCheck] = useState(false)

  const freeze = data?.freeze
  const checks = freeze?.checks || []
  const snapshotCount = data?.snapshot_count || 0
  const status = String(freeze?.status || "").toUpperCase()
  const isEditable = status === "ACTIVE" || status === "REJECTED"
  const isFinal = status === "POSTED" || status === "CANCELED"
  const canApprove = can("stock-opname.approve") || can("stock-adjustment.approve")

  const createCheck = async () => {
    setCreatingCheck(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks`,
        { notes: newCheckNotes || undefined }
      )
      toast.success(res.data?.message || "Cek stock dibuat")
      const newCheckId = res.data?.data?.id
      setShowAddCheck(false)
      setNewCheckNotes("")
      await mutate()
      if (newCheckId) navigate(`/stock-opname/${id}/checks/${newCheckId}`)
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Gagal membuat cek stock"))
    } finally {
      setCreatingCheck(false)
    }
  }

  const deleteCheck = async (check) => {
    if (!window.confirm(`Hapus cek stock ${check.check_no}?`)) return
    try {
      await axios.delete(`${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/checks/${check.id}`)
      toast.success("Cek stock dihapus")
      await mutate()
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Gagal menghapus cek stock"))
    }
  }

  const runAction = async () => {
    if (!confirmAction) return
    setActionLoading(true)
    try {
      let payload = {}
      if (confirmAction === "cancel") payload = { reason: cancelReason || undefined }
      if (confirmAction === "reject") {
        if (!rejectReason.trim()) {
          toast.error("Alasan tolak wajib diisi")
          setActionLoading(false)
          return
        }
        payload = { reason: rejectReason.trim() }
      }
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-opname/${id}/${confirmAction}`,
        payload
      )
      toast.success(res.data?.message || "Berhasil")
      setConfirmAction(null)
      setCancelReason("")
      setRejectReason("")
      await mutate()
    } catch (err) {
      toast.error(extractApiErrorMessage(err, "Aksi gagal"))
    } finally {
      setActionLoading(false)
    }
  }

  if (isLoading || !freeze) {
    return <div className="p-10 text-sm text-center text-neutral-500">Memuat detail freeze...</div>
  }

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-neutral-500">
              <Link to="/stock-opname" className="hover:underline">Stock Opname</Link>
              <span>/</span>
              <span>{freeze.freeze_no}</span>
            </div>
            <h2 className="mt-1 text-lg font-semibold text-neutral-900">
              {freeze.freeze_no}
              <span
                className={`ml-3 inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
                  STATUS_BADGE[status] || STATUS_BADGE.CANCELED
                }`}
              >
                {STATUS_LABEL[status] || status}
              </span>
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Outlet <strong>{freeze.outlet_name || freeze.outlet_id}</strong>
              {" · "}Dibekukan {freeze.frozen_at ? moment(freeze.frozen_at).format("DD/MM/YYYY HH:mm") : "-"}
              {" · "}{snapshotCount} item snapshot
            </p>
            {freeze.notes && (
              <p className="mt-2 text-sm text-neutral-600">Catatan: {freeze.notes}</p>
            )}
            {freeze.submitted_at && (
              <p className="mt-1 text-xs text-neutral-500">
                Disubmit {moment(freeze.submitted_at).format("DD/MM/YYYY HH:mm")} oleh {freeze.submitted_by || "-"}
              </p>
            )}
            {freeze.approved_at && (
              <p className="mt-1 text-xs text-emerald-700">
                Disetujui {moment(freeze.approved_at).format("DD/MM/YYYY HH:mm")} oleh {freeze.approved_by || "-"}
              </p>
            )}
            {freeze.rejected_at && (
              <p className="mt-1 text-xs text-rose-700">
                Ditolak {moment(freeze.rejected_at).format("DD/MM/YYYY HH:mm")} oleh {freeze.rejected_by || "-"}
              </p>
            )}
            {freeze.reject_reason && status === "REJECTED" && (
              <div className="p-3 mt-2 text-sm border rounded-lg border-rose-200 bg-rose-50 text-rose-900">
                <strong>Alasan tolak HO:</strong> {freeze.reject_reason}
              </div>
            )}
            {freeze.cancel_reason && (
              <p className="mt-1 text-sm text-rose-600">Alasan batal: {freeze.cancel_reason}</p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/stock-opname/${id}/variance`}
              className="px-3 py-2 text-sm font-medium text-blue-700 border rounded-lg bg-blue-50 border-blue-200"
            >
              Cek Selisih
            </Link>

            {/* Submit untuk approval */}
            {(status === "ACTIVE" || status === "REJECTED") && (
              <button
                type="button"
                onClick={() => setConfirmAction("submit")}
                className="px-3 py-2 text-sm font-medium text-white rounded-lg bg-blue-600 hover:bg-blue-700"
              >
                {status === "REJECTED" ? "Resubmit ke HO" : "Submit ke HO"}
              </button>
            )}

            {/* HO actions */}
            {status === "SUBMITTED" && canApprove && (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmAction("approve")}
                  className="px-3 py-2 text-sm font-medium text-white rounded-lg bg-emerald-600 hover:bg-emerald-700"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmAction("reject")}
                  className="px-3 py-2 text-sm font-medium text-rose-700 border rounded-lg bg-rose-50 border-rose-200"
                >
                  Tolak
                </button>
              </>
            )}

            {/* Posting setelah approved */}
            {status === "APPROVED" && (
              <button
                type="button"
                onClick={() => setConfirmAction("post")}
                className="px-3 py-2 text-sm font-medium text-white rounded-lg bg-emerald-600 hover:bg-emerald-700"
              >
                Posting
              </button>
            )}

            {/* Cancel selalu tersedia kecuali final */}
            {!isFinal && (
              <button
                type="button"
                onClick={() => setConfirmAction("cancel")}
                className="px-3 py-2 text-sm font-medium text-red-700 border rounded-lg bg-red-50 border-red-200"
              >
                Batalkan Freeze
              </button>
            )}
          </div>
        </div>

        {confirmAction && (
          <div className="p-4 mt-4 border rounded-xl border-amber-200 bg-amber-50">
            {confirmAction === "post" && (
              <p className="text-sm font-medium text-amber-900">
                Posting akan mengupdate actual_stock semua produk di outlet sesuai cek stock. Item yang tidak dicek akan jadi 0. Lanjutkan?
              </p>
            )}
            {confirmAction === "submit" && (
              <p className="text-sm font-medium text-amber-900">
                Submit ke HO untuk approval. Selama menunggu, cek stock tidak bisa diubah. Lanjutkan?
              </p>
            )}
            {confirmAction === "approve" && (
              <p className="text-sm font-medium text-amber-900">
                Approve cek stock outlet. Setelah ini outlet bisa posting. Lanjutkan?
              </p>
            )}
            {confirmAction === "reject" && (
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Tolak cek stock. Outlet bisa edit lalu resubmit. Wajib isi alasan penolakan.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Alasan penolakan (wajib)..."
                  rows={3}
                  className="block w-full px-3 py-2 mt-2 text-sm border rounded-lg border-amber-300 bg-white"
                />
              </div>
            )}
            {confirmAction === "cancel" && (
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Pembatalan akan sinkronkan actual_stock dari current_stock (apply semua transaksi POS selama freeze). Lanjutkan?
                </p>
                <input
                  type="text"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Alasan batal (opsional)"
                  className="block w-full px-3 py-2 mt-2 text-sm border rounded-lg border-amber-300 bg-white"
                />
              </div>
            )}
            <div className="flex items-center gap-2 mt-3">
              <button
                type="button"
                onClick={runAction}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 ${
                  confirmAction === "cancel" || confirmAction === "reject"
                    ? "bg-red-600 hover:bg-red-700"
                    : confirmAction === "post" || confirmAction === "approve"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {actionLoading
                  ? "Memproses..."
                  : confirmAction === "post"
                  ? "Ya, Posting"
                  : confirmAction === "submit"
                  ? "Ya, Submit"
                  : confirmAction === "approve"
                  ? "Ya, Approve"
                  : confirmAction === "reject"
                  ? "Ya, Tolak"
                  : "Ya, Batalkan"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmAction(null)
                  setCancelReason("")
                  setRejectReason("")
                }}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium text-neutral-700 border rounded-lg border-neutral-300"
              >
                Batal
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h3 className="text-sm font-semibold text-neutral-900">Daftar Cek Stock</h3>
          {isEditable && (
            <button
              type="button"
              onClick={() => setShowAddCheck((v) => !v)}
              className="px-3 py-2 text-sm font-medium text-white rounded-lg bg-brand-900"
            >
              {showAddCheck ? "Tutup" : "Tambah Cek Stock"}
            </button>
          )}
        </div>

        {showAddCheck && (
          <div className="p-4 border-b border-neutral-200 bg-neutral-50">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="md:col-span-2">
                <input
                  type="text"
                  value={newCheckNotes}
                  onChange={(e) => setNewCheckNotes(e.target.value)}
                  placeholder="Catatan cek stock (opsional)"
                  className="block w-full px-3 py-2 text-sm border rounded-lg border-neutral-300"
                />
              </div>
              <button
                type="button"
                onClick={createCheck}
                disabled={creatingCheck}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {creatingCheck ? "Memproses..." : "Buat Cek Stock"}
              </button>
            </div>
          </div>
        )}

        {checks.length === 0 ? (
          <div className="p-10 text-sm text-center text-neutral-500">
            Belum ada cek stock. {isEditable && "Klik \"Tambah Cek Stock\" untuk mulai mencatat hasil cek fisik."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50">
                <tr className="text-left text-neutral-600">
                  <th className="px-5 py-3 font-medium">No Cek</th>
                  <th className="px-5 py-3 font-medium">Tanggal</th>
                  <th className="px-5 py-3 font-medium">Catatan</th>
                  <th className="px-5 py-3 font-medium text-right">Item</th>
                  <th className="px-5 py-3 font-medium">Dibuat Oleh</th>
                  <th className="px-5 py-3 font-medium text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c) => (
                  <tr key={c.id} className="border-t border-neutral-100">
                    <td className="px-5 py-3 font-medium text-neutral-900">{c.check_no}</td>
                    <td className="px-5 py-3 text-neutral-700">
                      {c.check_date ? moment(c.check_date).format("DD/MM/YYYY HH:mm") : "-"}
                    </td>
                    <td className="px-5 py-3 text-neutral-700">{c.notes || "-"}</td>
                    <td className="px-5 py-3 text-right text-neutral-700">{c.items_count ?? 0}</td>
                    <td className="px-5 py-3 text-neutral-700">{c.created_by || "-"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Link
                          to={`/stock-opname/${id}/checks/${c.id}`}
                          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg"
                        >
                          {isEditable ? "Edit" : "Lihat"}
                        </Link>
                        {isEditable && (
                          <button
                            type="button"
                            onClick={() => deleteCheck(c)}
                            className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
                          >
                            Hapus
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
